import { useState, useEffect, useRef, useContext } from 'react';
import { useLocation, useNavigate, UNSAFE_NavigationContext } from 'react-router-dom';

interface UseNavigationGuardProps {
  shouldBlock: boolean; // returned from your dirty-check logic
}

export const useNavigationGuard = ({ shouldBlock }: UseNavigationGuardProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { navigator } = useContext(UNSAFE_NavigationContext) as any;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);

  const isNavigatingRef = useRef(false);
  const unblockRef = useRef<null | (() => void)>(null);

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlock) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldBlock]);

  // Handle in-app navigation
  useEffect(() => {
    if (!shouldBlock) {
      unblockRef.current?.();
      unblockRef.current = null;
      return;
    }

    // Intercept React Router navigation
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    navigator.push = (...args: any[]) => {
      if (isNavigatingRef.current) {
        originalPush.apply(navigator, args);
        return;
      }

      const [to] = args;
      const targetPath = typeof to === 'string' 
        ? new URL(to, window.location.origin).pathname 
        : new URL(to.pathname, window.location.origin).pathname;
      
      const currentPath = location.pathname;

      if (targetPath !== currentPath) {
        setPendingPath(typeof to === 'string' ? to : to.pathname); // Store the raw path
        setIsModalOpen(true);
        return;
      }

      originalPush.apply(navigator, args);
    };

    navigator.replace = (...args: any[]) => {
      if (isNavigatingRef.current) {
        originalReplace.apply(navigator, args);
        return;
      }

      const [to] = args;
      const targetPath = typeof to === 'string' 
        ? new URL(to, window.location.origin).pathname 
        : new URL(to.pathname, window.location.origin).pathname;
      
      const currentPath = location.pathname;

      if (targetPath !== currentPath) {
        setPendingPath(typeof to === 'string' ? to : to.pathname);
        setIsModalOpen(true);
        return;
      }

      originalReplace.apply(navigator, args);
    };

    // Handle back/forward buttons
    const handlePopState = (event: PopStateEvent) => {
      if (!isNavigatingRef.current && shouldBlock) {
        // We can't strictly prevent popstate, but we can revert it
        // Note: This is tricky because the URL has already changed by the time we get here
        event.preventDefault();
        window.history.pushState(null, '', location.pathname); // Revert URL
        setPendingPath(null); // Back button doesn't have a clear target path we can easily resume without history manipulation
        setIsModalOpen(true);
      }
    };

    window.addEventListener('popstate', handlePopState);

    unblockRef.current = () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
      window.removeEventListener('popstate', handlePopState);
    };

    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [shouldBlock, navigator, location.pathname]);

  // User confirms navigation
  const confirmNavigation = () => {
    isNavigatingRef.current = true;

    if (pendingPath) {
      navigate(pendingPath);
    } else {
      // For back button interception, we might not have a pending path
      // In that case, we just close the modal. 
      // Ideally, we would go back: navigate(-1), but we reverted the URL change already.
      // If the user wanted to go back, we should probably do navigate(-1) here?
      // But simplifying: just close modal if no path (user stays).
    }

    setIsModalOpen(false);
    setPendingPath(null);
    
    // Reset flag after a tick to allow navigation to complete
    setTimeout(() => {
        isNavigatingRef.current = false;
    }, 0);
  };

  // User cancels navigation
  const cancelNavigation = () => {
    setIsModalOpen(false);
    setPendingPath(null);
  };

  return {
    isModalOpen,
    confirmNavigation,
    cancelNavigation,
  };
};
